/**
 * Money, in and out, with no floating point anywhere in the middle.
 *
 * The API carries every amount as **integer paise** — `sellingPaise: 2500` is
 * ₹25.00 — and Constitution Principle VI says so for the reason every POS
 * eventually learns: `0.1 + 0.2` is not `0.3`, and a till that computes a total
 * in rupees is a till whose cash drawer disagrees with its own report by a paisa
 * a day until somebody has to explain it.
 *
 * A person still types rupees, so exactly two conversions exist and both live
 * here. **Neither of them divides or multiplies a decimal.** `Number('25.10') *
 * 100` is `2509.9999999999995`, which `Math.round` rescues and which the next
 * person to write the same line will not know to rescue — so the digits are
 * moved as text and the arithmetic only ever runs on integers.
 *
 * Separated from `field-schemas.ts` the way `pagination.ts` is separated from
 * `Pager.tsx`: the sums are what a second screen reuses and what a reader has to
 * be able to check, and a zod fragment is a poor place to read arithmetic.
 */

/**
 * What a person may type into a price field: rupees, optionally paise, and
 * nothing else.
 *
 * No sign, because `CATALOGUE_PRICE_INVALID` is what a negative price earns and
 * refusing it at the field is a better sentence than a round trip. No thousands
 * separators, no `₹` — the symbol is on the label, and accepting it in the value
 * would mean deciding what `₹₹25` means.
 */
export const RUPEES_SHAPE = /^\d+(\.\d{1,2})?$/;

/**
 * Rupees as typed, in paise.
 *
 * **Assumes `RUPEES_SHAPE` has already matched.** It is called from the schema
 * fragment that enforces it and from the submit handler behind that same
 * schema; given anything else it answers `NaN`, which
 * {@link isRepresentablePaise} then refuses rather than sending.
 *
 * The fraction is padded rather than parsed as a decimal: `25.5` is fifty paise
 * and `25.05` is five, and the difference between them is which end the padding
 * goes on. Getting that backwards is a tenfold price error that renders
 * perfectly.
 */
export function paiseFromRupees(rupees: string): number {
  const [whole = '', fraction = ''] = rupees.trim().split('.');

  return Number(whole) * 100 + Number(fraction.padEnd(2, '0'));
}

/**
 * Whether an amount is one this client can carry without losing a paisa.
 *
 * **Not a business rule, and deliberately not worded as one.** The API's own
 * bound is its own — it refuses a negative or oversized amount with
 * `CATALOGUE_PRICE_INVALID` and this app does not restate what that bound is.
 * What this refuses is different and narrower: past `Number.MAX_SAFE_INTEGER`
 * the value stops being the value that was typed *before the request is built*,
 * so sending it would be sending a number nobody chose. Refusing what cannot be
 * represented is not adding a rule the API does not have.
 */
export const isRepresentablePaise = (paise: number): boolean => Number.isSafeInteger(paise);

/**
 * Paise back into the rupees a person types: `2500` becomes `'25.00'`.
 *
 * The inverse of {@link paiseFromRupees} and the shape a price field holds, so
 * it round-trips: `paiseFromRupees(rupeesFromPaise(n)) === n` for every safe
 * integer. That is the whole reason it exists rather than each screen reaching
 * for `formatPaise` — **that one is for reading and this one is for editing**,
 * and they must not be confused. `formatPaise` adds `₹` and Indian grouping,
 * both of which `RUPEES_SHAPE` refuses, so prefilling a field with it produces a
 * form that is invalid the moment it opens.
 *
 * Digits again, never a division. Repricing prefills the cost that did not move
 * from the price in force, and a prefill that is a paisa out is a price change
 * nobody typed.
 */
export function rupeesFromPaise(paise: number): string {
  const whole = Math.round(paise);

  const digits = String(Math.abs(whole)).padStart(3, '0');

  // A plain hyphen here and not the minus sign `formatPaise` renders: this
  // value goes back into an input, where it has to be what a person could have
  // typed. Nothing in this app should produce a negative price at all.
  return `${whole < 0 ? '-' : ''}${digits.slice(0, -2)}.${digits.slice(-2)}`;
}

/** Indian digit grouping — 1,00,000 rather than 100,000. */
const GROUPING = new Intl.NumberFormat('en-IN');

/**
 * Paise as an amount somebody reads: `2500` becomes `₹25.00`.
 *
 * Always two decimal places, including for a whole number of rupees and for
 * zero — `₹0.00` is a free child ticket said plainly, and a column of amounts
 * that sometimes has a decimal point and sometimes does not cannot be compared
 * down its length. Pair it with the `numeric` utility wherever it sits in a
 * column, per `docs/ui-conventions.md`.
 *
 * The split is on the digits rather than on a division for the reason at the top
 * of this file. `Math.round` guards the one thing the type cannot: the generated
 * `sellingPaise` is `number`, and a non-integer arriving there would otherwise
 * render as `₹25.5.00`.
 */
export function formatPaise(paise: number): string {
  const whole = Math.round(paise);
  const negative = whole < 0;

  // `String(Math.abs(…))` on a safe integer is plain digits — no exponent, no
  // separator — which is what makes the slicing below exact.
  const digits = String(Math.abs(whole)).padStart(3, '0');
  const rupees = digits.slice(0, -2);
  const fraction = digits.slice(-2);

  // A true minus sign, not a hyphen. Nothing in this app should produce a
  // negative price, and if one ever arrives it should look wrong rather than
  // look like a code.
  return `${negative ? '−' : ''}₹${GROUPING.format(Number(rupees))}.${fraction}`;
}
